import React, { useEffect, useMemo, useState } from "react";
import DashboardShell from "./components/DashboardShell";

const DOC_TYPES = [
  { value: "tax_refund_affidavit", label: "Tax Refund Affidavit" },
  { value: "affidavit", label: "Affidavit (Other)" },
  { value: "worksheet", label: "Worksheet" },
  { value: "other", label: "Other" },
];

export default function TrusteeDocTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [showSchemaModal, setShowSchemaModal] = useState(false);
  const [schemaTemplate, setSchemaTemplate] = useState(null);

  const [form, setForm] = useState({
    name: "",
    doc_type: "tax_refund_affidavit",
    description: "",
    default_checklist_name: "",
    default_checklist_description: "",
    field_schema: [],
    is_active: true,
    file: null,
  });

  const API_URL = process.env.REACT_APP_API_URL;
  // NOTE: If your firm token key differs, change it here.
  const firmToken = localStorage.getItem("accessToken") || localStorage.getItem("firmToken") || "";

  const apiFetch = async (path, options = {}) => {
    const url = `${API_URL}${path}`;
    const headers = new Headers(options.headers || {});

    if (firmToken) headers.set("Authorization", `Bearer ${firmToken}`);
    // If you use a Version header in your API, uncomment:
    // if (process.env.REACT_APP_VERSION) headers.set("Version", process.env.REACT_APP_VERSION);

    const res = await fetch(url, {
      ...options,
      headers,
    });

    // Try to parse JSON when possible
    const contentType = res.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");
    const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

    if (!res.ok) {
      const msg = (data && data.detail) || (typeof data === "string" && data) || `Request failed (${res.status})`;
      throw new Error(msg);
    }

    return data;
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/firm/trustee-doc-templates/");
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load templates", e);
      alert(e?.message || "Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line
  }, []);

  const onChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const openAddTemplate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditTemplate = (tpl) => {
    setIsEditing(true);
    setEditingId(tpl.id);
    setForm({
      name: tpl.name || "",
      doc_type: tpl.doc_type || "tax_refund_affidavit",
      description: tpl.description || "",
      default_checklist_name: tpl.default_checklist_name || "",
      default_checklist_description: tpl.default_checklist_description || "",
      field_schema: Array.isArray(tpl.field_schema) ? tpl.field_schema : [],
      is_active: !!tpl.is_active,
      file: null, // optional on edit
    });
    setNewField({ key: "", label: "", type: "text", required: false, help: "" });
    setShowModal(true);
  };

  const openSchema = (tpl) => {
    setSchemaTemplate(tpl);
    setShowSchemaModal(true);
  };

  const downloadTemplate = async (tpl) => {
    try {
      // expected: { url: "https://..." }
      const data = await apiFetch(`/firm/trustee-doc-templates/${tpl.id}/presign/`);
      const url = (data && (data.url || data.download_url)) || (typeof data === "string" ? data : null);
      if (!url) return alert("No download URL returned.");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Template download failed", e);
      alert(e?.message || "Failed to download template.");
    }
  };
  const resetForm = () => {
    setForm({
      name: "",
      doc_type: "tax_refund_affidavit",
      description: "",
      default_checklist_name: "",
      default_checklist_description: "",
      field_schema: [],
      is_active: true,
      file: null,
    });
    setIsEditing(false);
    setEditingId(null);
  };
  const [newField, setNewField] = useState({
    key: "",
    label: "",
    type: "text",
    required: false,
    help: "",
  });

  const addSchemaField = () => {
    const key = String(newField.key || "").trim();
    const label = String(newField.label || "").trim();
    if (!key) return alert("Custom field key is required (e.g., TAX_YEAR_1_REFUND_AMOUNT)");
    if (!/^[A-Z0-9_]+$/.test(key)) {
      return alert("Field key must be UPPERCASE_SNAKE_CASE (A-Z, 0-9, underscore).");
    }
    if (!label) return alert("Custom field label is required.");

    // Prevent duplicates
    const exists = (form.field_schema || []).some(f => String(f?.key || "") === key);
    if (exists) return alert("That field key already exists in this template.");

    const field = {
      key,
      label,
      type: newField.type || "text",
      required: !!newField.required,
      help: String(newField.help || "").trim(),
    };

    setForm(prev => ({ ...prev, field_schema: [...(prev.field_schema || []), field] }));
    setNewField({ key: "", label: "", type: "text", required: false, help: "" });
  };

  const removeSchemaField = (key) => {
    setForm(prev => ({
      ...prev,
      field_schema: (prev.field_schema || []).filter(f => String(f?.key || "") !== String(key)),
    }));
  };

    const saveTemplate = async () => {
    if (!form.name.trim()) return alert("Template name is required.");

    // On create: file required
    if (!isEditing && !form.file) return alert("Please select a .docx template file.");

    setSaving(true);
    try {
      const path = isEditing && editingId
        ? `/firm/trustee-doc-templates/${editingId}/`
        : "/firm/trustee-doc-templates/";

      // If no file is being uploaded, send JSON so field_schema is real JSON (not a string)
      if (!form.file) {
        const payload = {
          name: form.name,
          doc_type: form.doc_type,
          description: form.description || "",
          default_checklist_name: form.default_checklist_name || "",
          default_checklist_description: form.default_checklist_description || "",
          field_schema: Array.isArray(form.field_schema) ? form.field_schema : [],
          is_active: !!form.is_active,
        };

        await apiFetch(path, {
          method: isEditing ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        // Use FormData when uploading/replacing the .docx file
        const fd = new FormData();
        fd.append("name", form.name);
        fd.append("doc_type", form.doc_type);
        fd.append("description", form.description || "");
        fd.append("default_checklist_name", form.default_checklist_name || "");
        fd.append("default_checklist_description", form.default_checklist_description || "");
        // IMPORTANT: Some backends validate JSONField strictly on multipart.
        // Only send field_schema when there are actual custom fields.
        const schemaArr = Array.isArray(form.field_schema) ? form.field_schema : [];
        if (schemaArr.length > 0) {
          fd.append("field_schema", JSON.stringify(schemaArr));
        }
        fd.append("is_active", String(!!form.is_active));
        fd.append("template_file", form.file);

        await apiFetch(path, {
          method: isEditing ? "PATCH" : "POST",
          body: fd,
        });
      }

      setShowModal(false);
      resetForm();
      await loadTemplates();
    } catch (e) {
      console.error("Save template failed", e);
      alert(e?.message || "Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (tpl) => {
    try {
      await apiFetch(`/firm/trustee-doc-templates/${tpl.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !tpl.is_active }),
      });
      setTemplates(prev => prev.map(t => (t.id === tpl.id ? { ...t, is_active: !tpl.is_active } : t)));
    } catch (e) {
      console.error("Toggle active failed", e);
      alert(e?.message || "Failed to update template.");
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await apiFetch(`/firm/trustee-doc-templates/${tpl.id}/`, {
        method: "DELETE",
      });
      setTemplates(prev => prev.filter(t => t.id !== tpl.id));
    } catch (e) {
      console.error("Delete failed", e);
      alert(e?.message || "Failed to delete template.");
    }
  };

  const docTypeLabel = useMemo(() => {
    const map = new Map(DOC_TYPES.map(d => [d.value, d.label]));
    return (v) => map.get(v) || v;
  }, []);

  return (
    <DashboardShell>
      <div className="space-y-8">
        <div className="bg-white rounded shadow p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Trustee Doc Templates</h1>
              <p className="mt-1 text-sm text-gray-600">Upload Word templates used to generate trustee affidavits and worksheets.</p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={openAddTemplate}
                className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700"
              >
                + Add Template
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow p-6">
          <div className="text-gray-900 font-semibold">Word Template Placeholders</div>
          <p className="text-sm text-gray-600 mt-1">
            Use placeholders in your .docx like <span className="font-mono text-gray-900">{"{{CASE_NUMBER}}"}</span>. Field keys must be <span className="font-semibold">UPPERCASE_SNAKE_CASE</span>.
          </p>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Caption / Case</div>
              <ul className="mt-2 text-sm text-gray-800 font-mono space-y-1">
                <li>{"{{CASE_NUMBER}}"}</li>
                <li>{"{{CHAPTER}}"}</li>
                <li>{"{{COURT_DISTRICT}}"}</li>
                <li>{"{{DATE_FILED}}"}</li>
                <li>{"{{TODAY_DATE}}"}</li>
                <li>{"{{TODAY_DATE_LONG}}"}</li>
              </ul>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Debtors</div>
              <ul className="mt-2 text-sm text-gray-800 font-mono space-y-1">
                <li>{"{{DEBTOR_1_FULL_NAME}}"}</li>
                <li>{"{{DEBTOR_2_FULL_NAME}}"}</li>
                <li>{"{{DEBTOR_NAMES_CAPTION}}"}</li>
              </ul>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Meeting (341)</div>
              <ul className="mt-2 text-sm text-gray-800 font-mono space-y-1">
                <li>{"{{MEETING_DATE}}"}</li>
                <li>{"{{MEETING_TIME}}"}</li>
                <li>{"{{MEETING_ID}}"}</li>
                <li>{"{{MEETING_PASSCODE}}"}</li>
              </ul>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Filing Fee</div>
              <ul className="mt-2 text-sm text-gray-800 font-mono space-y-1">
                <li>{"{{FILING_FEE_AMOUNT}}"}</li>
                <li>{"{{FILING_FEES_PAID}}"}</li>
                <li>{"{{FILING_FEES_REMAINING}}"}</li>
              </ul>
            </div>
          </div>

          <p className="text-xs text-gray-600 mt-4">
            You can also add custom fields below when uploading a template. Custom fields become placeholders with the same key (e.g., <span className="font-mono">{"{{TAX_YEAR_1_REFUND_AMOUNT}}"}</span>).
          </p>
        </div>

        <section className="bg-white rounded shadow p-6">
          {loading ? (
            <div className="p-5 text-gray-600">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="p-5 text-gray-600">No templates yet.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {templates.map(tpl => (
                <div key={tpl.id} className="p-4 flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-gray-900 font-semibold">{tpl.name}</div>
                      <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 border border-gray-200">
                        {docTypeLabel(tpl.doc_type)}
                      </span>
                      {!tpl.is_active && (
                        <span className="text-xs px-2 py-1 rounded bg-yellow-50 text-yellow-800 border border-yellow-300">
                          Inactive
                        </span>
                      )}
                    </div>

                    {tpl.description && (
                      <div className="text-sm text-gray-600 mt-1">{tpl.description}</div>
                    )}

                    <div className="text-xs text-gray-600 mt-2">
                      Checklist default:{" "}
                      <span className="text-gray-800">{tpl.default_checklist_name || "—"}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      onClick={() => openSchema(tpl)}
                      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                    >
                      View Schema
                    </button>
                    <button
                      onClick={() => downloadTemplate(tpl)}
                      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                    >
                      Download
                    </button>
                    <button
                      onClick={() => openEditTemplate(tpl)}
                      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleActive(tpl)}
                      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                      title="Activate/Deactivate"
                    >
                      {tpl.is_active ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      onClick={() => deleteTemplate(tpl)}
                      className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4">
                        <div className="w-full max-w-2xl max-h-[90vh] rounded-lg bg-white shadow-xl p-6 flex flex-col">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xl font-bold text-gray-900">{isEditing ? "Edit Template" : "Add Template"}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {isEditing ? "Update template details, schema, or optionally upload a new .docx to replace the existing one." : `Upload a .docx with placeholders (e.g. ${"{{CASE_NUMBER}}"}).`}
                  </div>
                </div>
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="text-gray-500 hover:text-gray-800 text-xl leading-none"
                >
                  ×
                </button>
              </div>

                             <div className="mt-4 grid grid-cols-1 gap-3 overflow-y-auto pr-1 min-h-0">
                <label className="text-sm text-gray-700">
                  Name
                  <input
                    value={form.name}
                    onChange={e => onChange("name", e.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 bg-white text-gray-900 px-3 py-2"
                  />
                </label>

                <label className="text-sm text-gray-700">
                  Type
                  <select
                    value={form.doc_type}
                    onChange={e => onChange("doc_type", e.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 bg-white text-gray-900 px-3 py-2"
                  >
                    {DOC_TYPES.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </label>

                <label className="text-sm text-gray-700">
                  Description (optional)
                  <textarea
                    value={form.description}
                    onChange={e => onChange("description", e.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 bg-white text-gray-900 px-3 py-2"
                    rows={3}
                  />
                </label>

                <label className="text-sm text-gray-700">
                  Default checklist item name (optional)
                  <input
                    value={form.default_checklist_name}
                    onChange={e => onChange("default_checklist_name", e.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 bg-white text-gray-900 px-3 py-2"
                  />
                </label>

                <label className="text-sm text-gray-700">
                  Default checklist item description (optional)
                  <textarea
                    value={form.default_checklist_description}
                    onChange={e => onChange("default_checklist_description", e.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 bg-white text-gray-900 px-3 py-2"
                    rows={3}
                  />
                </label>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="text-sm font-semibold text-gray-900">Custom Fields (Optional)</div>
                  <p className="text-xs text-gray-600 mt-1">
                    Add any extra fields your template needs. Use the <span className="font-mono">KEY</span> in Word as <span className="font-mono">{"{{KEY}}"}</span>.
                  </p>

                  {(form.field_schema || []).length > 0 && (
                    <div className="mt-3 space-y-2">
                      {(form.field_schema || []).map((f) => (
                        <div key={f.key} className="flex items-start justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-2">
                          <div>
                            <div className="text-sm text-gray-900 font-mono">{f.key}</div>
                            <div className="text-xs text-gray-600">{f.label}{f.required ? " • required" : ""}{f.type ? ` • ${f.type}` : ""}</div>
                            {f.help ? <div className="text-xs text-gray-600 mt-1">{f.help}</div> : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSchemaField(f.key)}
                            className="p-2 rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                            title="Remove field"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="text-sm text-gray-700">
                      Key
                      <input
                        value={newField.key}
                        onChange={(e) => setNewField(prev => ({ ...prev, key: e.target.value.toUpperCase() }))}
                        placeholder="TAX_YEAR_1_REFUND_AMOUNT"
                        className="mt-1 w-full rounded border border-gray-300 bg-white text-gray-900 px-3 py-2 font-mono"
                      />
                    </label>

                    <label className="text-sm text-gray-700">
                      Type
                      <select
                        value={newField.type}
                        onChange={(e) => setNewField(prev => ({ ...prev, type: e.target.value }))}
                        className="mt-1 w-full rounded border border-gray-300 bg-white text-gray-900 px-3 py-2"
                      >
                        <option value="text">Text</option>
                        <option value="textarea">Long text</option>
                        <option value="number">Number</option>
                        <option value="currency">Currency</option>
                        <option value="date">Date</option>
                        <option value="select">Select</option>
                        <option value="boolean">Yes/No</option>
                      </select>
                    </label>

                    <label className="text-sm text-gray-700 sm:col-span-2">
                      Label
                      <input
                        value={newField.label}
                        onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
                        placeholder="Refund amount for tax year 2024"
                        className="mt-1 w-full rounded border border-gray-300 bg-white text-gray-900 px-3 py-2"
                      />
                    </label>

                    <label className="text-sm text-gray-700 sm:col-span-2">
                      Help text (optional)
                      <input
                        value={newField.help}
                        onChange={(e) => setNewField(prev => ({ ...prev, help: e.target.value }))}
                        placeholder="Shown to staff when creating this affidavit"
                        className="mt-1 w-full rounded border border-gray-300 bg-white text-gray-900 px-3 py-2"
                      />
                    </label>

                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!newField.required}
                        onChange={(e) => setNewField(prev => ({ ...prev, required: e.target.checked }))}
                      />
                      Required
                    </label>

                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={addSchemaField}
                        className="w-full px-3 py-2 rounded bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
                      >
                        Add Field
                      </button>
                    </div>
                  </div>
                </div>

                <label className="text-sm text-gray-700">
                  {`Template file (.docx)${isEditing ? " (optional)" : ""}`}
                  <input
                    type="file"
                    accept=".docx"
                    onChange={e => onChange("file", e.target.files?.[0] || null)}
                    className="mt-1 w-full text-gray-700"
                  />
                </label>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={e => onChange("is_active", e.target.checked)}
                  />
                  Active
                </label>
              </div>

                            <div className="mt-5 flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTemplate}
                  disabled={saving}
                  className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow ${saving ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700"}`}
                >
                  {saving ? "Saving..." : "Save Template"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showSchemaModal && schemaTemplate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4">
                        <div className="w-full max-w-2xl max-h-[90vh] rounded-lg bg-white shadow-xl p-6 flex flex-col">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xl font-bold text-gray-900">Template Schema</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {schemaTemplate.name} • {docTypeLabel(schemaTemplate.doc_type)}
                  </div>
                </div>
                <button
                  onClick={() => { setShowSchemaModal(false); setSchemaTemplate(null); }}
                  className="text-gray-500 hover:text-gray-800 text-xl leading-none"
                >
                  ×
                </button>
              </div>

              <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                {(Array.isArray(schemaTemplate.field_schema) && schemaTemplate.field_schema.length > 0) ? (
                  <div className="space-y-2">
                    {schemaTemplate.field_schema.map((f) => (
                      <div key={f.key} className="rounded border border-gray-200 bg-white px-3 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-mono text-sm text-gray-900">{`{{${f.key}}}`}</div>
                          <div className="text-xs text-gray-600">{f.type}{f.required ? " • required" : ""}</div>
                        </div>
                        <div className="text-sm font-semibold text-gray-900 mt-1">{f.label}</div>
                        {f.help ? <div className="text-sm text-gray-600 mt-1">{f.help}</div> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">No custom fields on this template.</div>
                )}
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  onClick={() => { setShowSchemaModal(false); setSchemaTemplate(null); }}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}